import { CSSProperties } from 'react';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { TableZ } from 'styles/zIndex';

import { seatEdgeSafeLeft } from './seatEdgeInsets';

/**
 * Seat styles are split into a positioned **wrapper** and an inner **content**
 * box. The wrapper owns the table-relative position and the responsive
 * `--seat-scale` (set on the table root); the content box is the flex layout and
 * the element GSAP animates (fade-in / win bounce). Keeping the responsive scale
 * off the GSAP-animated element prevents GSAP from overwriting it — the two
 * scales compose instead of fighting.
 */
export const getLandscapeModeStyles = (position: SeatPosition, disableTransition = false) => {
  // During the POV seat-assignment rotation, positions are stepped every frame
  // along the ellipse arc, so the smoothing transition is turned off to avoid
  // smearing/lag; it resumes for the usual single position changes.
  const transition = disableTransition ? 'none' : 'left 0.5s ease, top 0.5s ease';
  const getMyPlayerWrapperStyle = (): CSSProperties => ({
    position: 'absolute',
    left: seatEdgeSafeLeft(position.leftPct, false),
    top: `${position.topPct}%`,
    transform: 'translate(-50%, -50%) scale(var(--seat-scale, 1))',
    transformOrigin: 'center center',
    transition,
    // Table z-order model (Jason 8/13, EN-10420): the POV player's hand
    // outranks everything ON the felt — bet chips and the dealer button both
    // sit at z5 (objects on the table), the POV seat at z6 (cards in the
    // player's hand). Static, all situations — LandscapeCurrentPlayer's
    // tray-open-only z6 bump collapsed into this. Banded in styles/zIndex.ts by
    // EN-10520.
    zIndex: TableZ.seatPov,
    overflow: 'visible'
  });

  const getMyPlayerContentStyle = (): CSSProperties => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflow: 'visible'
  });

  const getOpponentWrapperStyle = (): CSSProperties => {
    const yAnchor = position.isTop ? '-40%' : position.isBottom ? '-30%' : '-55%';
    return {
      position: 'absolute',
      left: seatEdgeSafeLeft(position.leftPct, false),
      top: `${position.topPct}%`,
      transform: `translate(-50%, ${yAnchor}) scale(var(--seat-scale, 1))`,
      transformOrigin: 'center center',
      transition,
      // Seat band — above every table element (was 3, i.e. below the floating
      // dealer button, which is how it covered revealed cards).
      zIndex: TableZ.seat,
      overflow: 'visible'
    };
  };

  const getOpponentContentStyle = (): CSSProperties => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    overflow: 'visible'
  });

  return {
    getMyPlayerWrapperStyle,
    getMyPlayerContentStyle,
    getOpponentWrapperStyle,
    getOpponentContentStyle
  };
};
